import { fetchColorsForFilters } from '@/fetchers/mapping';
import useQueryParams from '@/hooks/useQueryParams';
import { ICategory, ICategoryFilters } from '@/models';
import { XIcon } from '@heroicons/react/solid';
import { filter, find, flatten, get, map, merge, omit } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  category: ICategory;
}

const AppliedFilters: React.FC<Props> = ({ category }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useQueryParams();
  const colorsMap = fetchColorsForFilters();

  const filters = omit(get(category, 'filters', {} as ICategoryFilters), [
    'color',
    'size'
  ]);

  const appliedFilters = useMemo(() => {
    return filter(
      flatten(
        map(
          Object.entries(omit(query, ['lang', 'categories', 'sort'])),
          ([key, value]) => {
            const values =
              typeof value === 'string' ? [value] : (value as string[]);

            if (key === 'color') {
              return values.map(color => ({
                key,
                label: colorsMap[color]?.color,
                value: colorsMap[color]?.code
              }));
            }

            if (key === 'size') {
              return values.map(value => ({
                key,
                label: value,
                value
              }));
            }

            if (Array.isArray(filters[key])) {
              return values.map(value => {
                return merge(
                  { key },
                  find(filters[key], (filter: any) => filter.value === value)
                );
              });
            }

            return values.map(value => ({
              key,
              label: filters[key]?.[value]?.label,
              value: filters[key]?.[value]?.value
            }));
          }
        )
      ),
      ({ value }) => typeof value === 'string'
    );
  }, [query]);

  function removeFilter({
    key,
    value
  }: {
    key: string;
    label: string;
    value: string;
  }) {
    const filter = query[key] as string | string[];

    setQuery({
      [key]: Array.isArray(filter) ? filter.filter(v => v !== value) : undefined
    });
  }

  function removeAllFilters() {
    setQuery(
      Object.keys(query).reduce(
        (acc, key) => ({ ...acc, [key]: undefined }),
        {} as Record<string, undefined>
      )
    );
  }

  if (appliedFilters.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3 py-6">
      <h3 className="text-sm font-bold uppercase tracking-uyn">
        {t('FILTERS.APPLIED')}
      </h3>

      <ul className="space-y-1.5">
        {appliedFilters.map((filter, idx) => (
          <li
            key={idx}
            className="w-max flex items-center space-x-3 text-sm bg-gray-100 rounded p-3"
          >
            <button onClick={() => removeFilter(filter)}>
              <XIcon className="w-3 h-3" />
            </button>

            <span>{filter.label}</span>
          </li>
        ))}
      </ul>

      <button
        onClick={removeAllFilters}
        className="text-gray-600 text-sm tracking-wide underline underline-offset-2"
      >
        {t('FILTERS.REMOVE')}
      </button>
    </div>
  );
};

export default AppliedFilters;
